import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useEngine } from '../../engine/api/context';
import { useNavSections } from '../../engine/shell/useNavSections';
import { Nova } from '../../engine/design/Nova';
import { Icon } from '../../engine/design/icons';

// Nova at the centre of Home with the top-level module areas fanned out around her — the
// "jump to any module" hub. The areas come from the same nav the sidebar uses (useNavSections:
// licensed modules, and on a connected system only the screens it backs), so a viewer only ever
// sees areas they can reach, and each
// card jumps to that area's first destination with a couple of quick sub-links under it.

function greeting(h: number): string {
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function NovaHub({ actions }: { actions?: ReactNode }): ReactNode {
  const { principal } = useEngine();
  const areas = useNavSections().areas.filter((a) => a.id !== 'home');
  const firstName = principal.name ? principal.name.split(' ')[0] : null;

  // Split the areas into two flanks so they sit on either side of Nova on wide screens.
  const half = Math.ceil(areas.length / 2);
  const flanks = [areas.slice(0, half), areas.slice(half)];

  const Card = ({ area }: { area: (typeof areas)[number] }) => {
    const items = area.sections.flatMap((s) => s.items);
    const to = items[0]?.to ?? '/';
    return (
      <div className="hf-nova-card">
        <Link to={to} className="hf-nova-card-head">
          <span className="hf-nova-card-icon" aria-hidden>
            <Icon name={area.icon} size={18} />
          </span>
          <span className="hf-nova-card-label hf-display">{area.label}</span>
          <Icon name="arrow-right" size={14} className="hf-nova-card-go" aria-hidden />
        </Link>
        <div className="hf-nova-card-links">
          {items.slice(0, 3).map((it) => (
            <Link key={it.to} to={it.to} className="hf-nova-card-link">
              {it.label}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section className="hf-nova-hub" aria-label="Jump to a module">
      <div className="hf-nova-flank">{flanks[0]!.map((a) => <Card key={a.id} area={a} />)}</div>
      <div className="hf-nova-center">
        <Nova variant="full" mood="wave" size={148} animated ground title="Nova, your NovaPSA guide" />
        <h1 className="hf-nova-greeting hf-display">
          {greeting(new Date().getHours())}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="hf-nova-tagline">Where would you like to go?</p>
        {actions && <div className="hf-nova-actions">{actions}</div>}
      </div>
      <div className="hf-nova-flank">{flanks[1]!.map((a) => <Card key={a.id} area={a} />)}</div>
    </section>
  );
}
